/* eslint-disable import/first, import/imports-first, import/order */
// BS needs to be imported before React and other imports:
// https://docs.bugsnag.com/platforms/browsers/react/#bundled
import BugsnagErrorBoundary from 'dpl/components/BugsnagErrorBoundary';

import React from 'react';

import initDevHooks from 'dpl/shared/utils/initDevHooks';

if (CONFIG.isDevelopment) {
  // eslint-disable-next-line global-require
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);

  initDevHooks();
}

import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ReactQueryConfigProvider, queryCache } from 'react-query';
import { ReactQueryDevtools } from 'react-query-devtools';

import { ME_PATH } from 'dpl/shared/constants/urls';
import generateQueryKey from 'dpl/shared/fetching/utils/generateQueryKey';
import { getRandomStr } from 'dpl/shared/utils';
import createReduxStore from 'dpl/util/createReduxStore';

const RQ_CONFIG = {
  queries: {
    retry: 0,
    cacheTime: Infinity, // how long to keep in cache even if not mounted
    refetchOnWindowFocus: false,
    staleTime: Infinity // how long before resource is marked as stale (needs refetch)
  }
};

const RQ_DEVTOOLS_ENABLED = window.location.search.includes('rqdebug');
let RQ_DEVTOOLS_MOUNTED = false;

const CURRENT_USER_QUERY_KEY = generateQueryKey({ url: ME_PATH });
function setCurrentUserIfNeeded() {
  if (!window.__TPT_CONNECT_CURRENT_USER_DATA || queryCache.getQueryData(CURRENT_USER_QUERY_KEY)) {
    return;
  }

  queryCache.setQueryData(CURRENT_USER_QUERY_KEY, { data: window.__TPT_CONNECT_CURRENT_USER_DATA });
}

export default function initReactApp(components = {}) {
  // allow us to reuse redux store across multiple packs
  let reduxStore = window.__DPL_REDUX_STORE;

  if (!reduxStore) {
    reduxStore = createReduxStore();
    window.__DPL_REDUX_STORE = reduxStore;

    if (!CONFIG.isProduction) {
      queryCache.subscribe((cache) => {
        // don't use cache.isFetching since { enabled: false } queries are marked as `isFetching`
        const inFlight = Object.values(cache.queries).reduce((acc, query) => {
          if (query.state.isIdle || query.state.isSuccess) {
            return acc;
          }
          return acc + 1;
        }, 0);
        document.body.setAttribute('data-test-react-query-in-flight', inFlight);
      });
    }
  }

  window.__DPL_DEBUG_SESSION_ID || (window.__DPL_DEBUG_SESSION_ID = getRandomStr());

  setCurrentUserIfNeeded();

  function createComponentTree(Component) {
    return (
      <BugsnagErrorBoundary>
        <Provider store={ reduxStore }>
          <ReactQueryConfigProvider config={ RQ_CONFIG }>
            <Component />
          </ReactQueryConfigProvider>
        </Provider>
        {
          (CONFIG.isDevelopment && RQ_DEVTOOLS_ENABLED && !RQ_DEVTOOLS_MOUNTED) && (() => {
            RQ_DEVTOOLS_MOUNTED = true;
            return <ReactQueryDevtools />;
          })()
        }
      </BugsnagErrorBoundary>
    );
  }

  Object.entries(components).forEach(([name, Component]) => {
    [...document.querySelectorAll(`[data-react-mount-component="${name}"]`)].forEach((mountEl) => {
      ReactDOM.render(createComponentTree(Component), mountEl);
    });
  });
}

/* eslint-enable import/first, import/imports-first */
